import { z } from 'zod';
import { PersonRef } from './people.js';
import { RetainerState } from './timesheets.js';
/**
 * Reports (docs/adr/0053-reports.md): read-only aggregates across clients, projects and people.
 * Dates are calendar dates (YYYY-MM-DD) in the workspace's time zone; `from` and `to` are both inclusive.
 * Every number is computed on the server; the client only draws and exports what it is sent.
 */
/** The longest range any report covers, so every query is bounded by date as well as tenant. */
export const REPORT_MAX_DAYS = 366;
export const ReportInterval = z.enum(['week', 'month']);
const rangeOk = (v) => {
    if (!v.from || !v.to)
        return true;
    const days = (Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`)) / 86_400_000;
    return days >= 0 && days < REPORT_MAX_DAYS;
};
/** The filters every report takes. Defaults: the twelve weeks ending this week, every client, project and person. */
export const ReportFilters = z
    .object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    clientId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    /** Members without team view may only pass their own id (anyone else's answers 404). */
    userId: z.uuid().optional(),
})
    .refine(rangeOk, { message: `Choose a range of at most ${REPORT_MAX_DAYS} days that ends after it starts`, path: ['to'] });
export const ReportTrendFilters = z
    .object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    clientId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    userId: z.uuid().optional(),
    interval: ReportInterval.optional(),
})
    .refine(rangeOk, { message: `Choose a range of at most ${REPORT_MAX_DAYS} days that ends after it starts`, path: ['to'] });
export const WorkloadFilters = z.object({
    clientId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    userId: z.uuid().optional(),
});
/** The resolved filters, echoed in every response. */
export const ReportScope = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    clientId: z.uuid().nullable(),
    projectId: z.uuid().nullable(),
    userId: z.uuid().nullable(),
    /** False: the viewer is a member without team view, so everything is their own (userId is theirs). */
    team: z.boolean(),
});
export const ReportPeriod = z.object({ start: z.iso.date(), last: z.iso.date() });
export const ReportSection = z.enum(['time', 'retainers', 'throughput', 'support', 'workload']);
export const ReportOptions = z.object({
    today: z.iso.date(),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]),
    defaultFrom: z.iso.date(),
    defaultTo: z.iso.date(),
    maxDays: z.number().int(),
    /** The viewer sees everyone (owner, admin, or a member with team view on). */
    team: z.boolean(),
    /** The workspace setting `reports.memberTeamView`. */
    memberTeamView: z.boolean(),
    /** Reports this viewer may open, in order (others answer 404). */
    sections: z.array(ReportSection),
    clients: z.array(z.object({ id: z.uuid(), name: z.string(), archived: z.boolean() })),
    projects: z.array(z.object({ id: z.uuid(), name: z.string(), clientId: z.uuid(), archived: z.boolean() })),
    /** Staff the viewer may filter by: everyone for team view, otherwise only themselves. */
    people: z.array(PersonRef),
});
// --- Time -----------------------------------------------------------------------------------------
export const TimeReportRow = z.object({ id: z.uuid().nullable(), name: z.string(), minutes: z.number().int() });
export const ReportsTime = z.object({
    scope: ReportScope,
    interval: ReportInterval,
    totalMinutes: z.number().int(),
    /** Every period in the range, including empty ones. */
    periods: z.array(ReportPeriod.extend({ minutes: z.number().int() })),
    /** `id` null: time on tasks with no client. */
    byClient: z.array(TimeReportRow),
    /** `id` null: time on tasks without a project (named after the client). */
    byProject: z.array(TimeReportRow.extend({ clientName: z.string() })),
    byPerson: z.array(z.object({ person: PersonRef, minutes: z.number().int() })),
    /**
     * Tasks with time in the range (at most 200, most time first): estimate against everything logged on the
     * task and against the time in the range.
     */
    estimates: z.array(z.object({
        workItemId: z.uuid(),
        key: z.string(),
        title: z.string(),
        projectName: z.string(),
        clientName: z.string(),
        estimateMinutes: z.number().int().nullable(),
        loggedMinutes: z.number().int(),
        rangeMinutes: z.number().int(),
    })),
    estimatesTruncated: z.boolean(),
    /** Of the time in the range: on tasks that have an estimate, and those tasks' estimates. */
    estimated: z.object({ loggedMinutes: z.number().int(), estimateMinutes: z.number().int() }),
    /**
     * Whether the workspace tracks billable time (`time.trackBillable`, docs/adr/0054). When it doesn't, every entry
     * counts as billable and the split is not shown.
     */
    billableTracked: z.boolean(),
    billableMinutes: z.number().int(),
    nonBillableMinutes: z.number().int(),
});
// --- Retainers ------------------------------------------------------------------------------------
export const ReportRetainerMonth = z.object({
    month: z.string(),
    retainerMinutes: z.number().int(),
    usedMinutes: z.number().int(),
    remainingMinutes: z.number().int(),
    overMinutes: z.number().int(),
    percent: z.number().int().nullable(),
    state: RetainerState,
});
export const ReportsRetainers = z.object({
    scope: ReportScope,
    /** Calendar months the range touches, oldest first. */
    months: z.array(z.string()),
    /** The current month (today), when it is in the range: its numbers are still growing. */
    currentMonth: z.string().nullable(),
    clients: z.array(z.object({
        clientId: z.uuid(),
        name: z.string(),
        months: z.array(ReportRetainerMonth),
        /** Over the whole range: used minus retainer (positive means over). */
        netMinutes: z.number().int(),
        usedMinutes: z.number().int(),
        retainerMinutes: z.number().int(),
        projects: z.array(z.object({ projectId: z.uuid(), name: z.string(), retainerMinutes: z.number().int(), months: z.array(ReportRetainerMonth) })),
    })),
    totals: z.array(ReportRetainerMonth),
});
// --- Throughput -----------------------------------------------------------------------------------
export const CycleStats = z.object({
    /** Completed items with a recorded start (a move into an In progress status). */
    count: z.number().int(),
    medianHours: z.number().nullable(),
    p85Hours: z.number().nullable(),
});
export const ThroughputGroup = z.object({
    created: z.number().int(),
    completed: z.number().int(),
    cycle: CycleStats,
    /** Open and past due today. */
    overdueNow: z.number().int(),
});
export const ReportsThroughput = z.object({
    scope: ReportScope,
    interval: ReportInterval,
    totals: ThroughputGroup.extend({ completedWithoutStart: z.number().int() }),
    periods: z.array(ReportPeriod.extend({
        created: z.number().int(),
        completed: z.number().int(),
        medianCycleHours: z.number().nullable(),
        /** Open and past due at the end of the period (today for the current one). */
        overdue: z.number().int(),
    })),
    /** `id` null: items without a project. */
    byProject: z.array(ThroughputGroup.extend({ id: z.uuid().nullable(), name: z.string(), clientName: z.string() })),
    /** `person` null: unassigned. */
    byPerson: z.array(ThroughputGroup.extend({ person: PersonRef.nullable() })),
});
// --- Support --------------------------------------------------------------------------------------
export const SlaStats = z.object({
    /** Tickets measured. */
    count: z.number().int(),
    medianMinutes: z.number().int().nullable(),
    /** Measured tickets with a deadline that met it, as a whole percentage; null when none had a deadline. */
    metPercent: z.number().int().nullable(),
    breaches: z.number().int(),
});
export const ReportsSupport = z.object({
    scope: ReportScope,
    interval: ReportInterval,
    totals: z.object({
        opened: z.number().int(),
        resolved: z.number().int(),
        firstResponse: SlaStats,
        resolution: SlaStats,
        /** Open now and past the resolution deadline (not counting tickets waiting on the client). */
        openBreachedNow: z.number().int(),
        openNow: z.number().int(),
    }),
    periods: z.array(ReportPeriod.extend({
        opened: z.number().int(),
        resolved: z.number().int(),
        responseBreaches: z.number().int(),
        resolutionBreaches: z.number().int(),
    })),
    /** `id` null: tickets without a client. */
    byClient: z.array(z.object({
        id: z.uuid().nullable(),
        name: z.string(),
        opened: z.number().int(),
        resolved: z.number().int(),
        firstResponse: SlaStats,
        resolution: SlaStats,
        openNow: z.number().int(),
    })),
});
// --- Workload -------------------------------------------------------------------------------------
export const WorkloadBucket = z.object({
    count: z.number().int(),
    estimateMinutes: z.number().int(),
    /** Items in the bucket with no estimate. */
    unestimated: z.number().int(),
});
export const ReportsWorkload = z.object({
    scope: ReportScope.omit({ from: true, to: true }),
    today: z.iso.date(),
    thisWeek: ReportPeriod,
    nextWeek: ReportPeriod,
    people: z.array(z.object({
        /** Null: open work nobody is assigned to. */
        person: PersonRef.nullable(),
        /** Weekly capacity in minutes (`time.weeklyCapacityHours`); null when Time is off or for unassigned work. */
        capacityMinutes: z.number().int().nullable(),
        overdue: WorkloadBucket,
        thisWeek: WorkloadBucket,
        nextWeek: WorkloadBucket,
        later: WorkloadBucket,
        noDate: WorkloadBucket,
    })),
});
//# sourceMappingURL=reports.js.map